import React, { useEffect, useState, useMemo, useCallback } from 'react';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import {
  B2_ASPEKTE_CHAPTERS,
  VOCAB_SETS,
  getCourses,
  getLessons,
  getEntries,
  getStats,
  shuffleArray,
} from '../data/vocab';
import {
  BookOpenIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SparklesIcon,
  ListBulletIcon,
  AcademicCapIcon,
  BookmarkIcon,
} from '@heroicons/react/24/outline';
import PageHeader from '../components/ui/PageHeader';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { unknownWordsAPI } from '../services/api';
import SpeakButton from '../components/ui/SpeakButton';
import { reviewWord, GRADE_LABELS, SRS_GRADES } from '../utils/srsEngine';

const selectClassName = cn(
  'flex h-11 w-full appearance-none rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 pr-10 text-sm font-semibold text-zinc-800 shadow-sm transition-colors hover:border-zinc-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/15 disabled:cursor-not-allowed disabled:bg-zinc-50 disabled:opacity-60',
);
const VOCAB_PAGE_SIZE = 30;

const UnknownWordToggle = ({ saved, disabled, onClick, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || saved}
    title={saved ? 'Në listën tënde' : 'Shto te fjalët që nuk i di'}
    aria-label={saved ? 'Në listën tënde' : 'Shto te fjalët që nuk i di'}
    className={`shrink-0 p-2 rounded-full transition-colors duration-200 cursor-pointer disabled:cursor-default ${
      saved
        ? 'text-emerald-600 bg-emerald-50'
        : 'text-brand-text/35 hover:text-brand-primary hover:bg-brand-primary/10'
    } ${className}`}
  >
    <BookmarkIcon className={`w-5 h-5 ${saved ? 'fill-current' : ''}`} />
  </button>
);

const MODES = [
  { id: 'browse', label: 'Lista', icon: ListBulletIcon },
  { id: 'flashcards', label: 'Flashcards', icon: BookOpenIcon },
  { id: 'quiz', label: 'Kuiz', icon: SparklesIcon },
];

const VocabularyPage = () => {
  const { user, updateUserStats } = useAuth();
  const [unknownByWord, setUnknownByWord] = useState({});
  const [unknownLoading, setUnknownLoading] = useState(false);

  const [vocabSet, setVocabSet] = useState('a1');
  const [mode, setMode] = useState('browse');
  const [selectedChapter, setSelectedChapter] = useState('all');
  const [selectedModule, setSelectedModule] = useState('all');
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [selectedLesson, setSelectedLesson] = useState('all');
  const [search, setSearch] = useState('');
  const [includePhrases, setIncludePhrases] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const setInfo = VOCAB_SETS[vocabSet];
  const isSchritte = vocabSet === 'a1' || vocabSet === 'a2' || vocabSet === 'b1';

  const courses = getCourses(vocabSet);
  const schritteLessons = getLessons(vocabSet, selectedCourse);
  const chapter = B2_ASPEKTE_CHAPTERS.find((c) => c.id === selectedChapter);
  const modules = chapter?.modules ?? [];

  const filterOptions = useMemo(() => {
    if (isSchritte) {
      return {
        courseId: selectedCourse === 'all' ? null : selectedCourse,
        lessonIds: selectedLesson === 'all' ? null : [selectedLesson],
      };
    }
    return {
      chapterIds: selectedChapter === 'all' ? null : [selectedChapter],
      moduleId: selectedModule === 'all' ? null : selectedModule,
      includePhrases,
    };
  }, [isSchritte, selectedCourse, selectedLesson, selectedChapter, selectedModule, includePhrases]);

  const stats = useMemo(() => getStats(vocabSet, filterOptions), [vocabSet, filterOptions]);

  const entries = useMemo(
    () => getEntries(vocabSet, filterOptions),
    [vocabSet, filterOptions]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return entries;
    const q = search.toLowerCase();
    return entries.filter(
      (e) => e.de.toLowerCase().includes(q) || e.sq.toLowerCase().includes(q)
    );
  }, [entries, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filtered]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / VOCAB_PAGE_SIZE));
  const pageEntries = useMemo(
    () => filtered.slice((currentPage - 1) * VOCAB_PAGE_SIZE, currentPage * VOCAB_PAGE_SIZE),
    [filtered, currentPage]
  );

  const handleSetChange = (id) => {
    setVocabSet(id);
    setSelectedChapter('all');
    setSelectedModule('all');
    setSelectedCourse('all');
    setSelectedLesson('all');
    setSearch('');
  };

  useEffect(() => {
    let mounted = true;
    setUnknownLoading(true);
    unknownWordsAPI
      .list()
      .then((res) => {
        if (!mounted) return;
        const items = res.data?.items || [];
        const map = {};
        items.forEach((it) => {
          map[it.word] = it.id;
        });
        setUnknownByWord(map);
      })
      .catch(() => {
        // ignore: user might not be logged in yet
      })
      .finally(() => {
        if (!mounted) return;
        setUnknownLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const saveUnknown = useCallback(
    async (entry) => {
      if (!entry?.de) return;
      if (unknownByWord[entry.de]) return;
      try {
        const res = await unknownWordsAPI.add({
          word: entry.de,
          context: entry.sq || undefined,
          source: 'vocabulary',
          level: vocabSet,
        });
        setUnknownByWord((m) => ({ ...m, [entry.de]: res.data?.id || true }));
      } catch (e) {
        // already saved or not logged in
      }
    },
    [unknownByWord, vocabSet],
  );

  return (
    <DashboardLayout>
      <div className="page-shell-wide">
        <PageHeader
          icon={AcademicCapIcon}
          title={setInfo.title}
          description="Zgjidh nivelin dhe leksionin që dëshiron të studiosh. Fjalët shfaqen me përkthimin në shqip."
        >
          <div className="flex flex-wrap gap-2 mt-3">
            <Badge variant="outline" className="text-xs">{stats.chapters} {setInfo.unitsLabel}</Badge>
            <Badge variant="outline" className="text-xs">{stats.total} fjalë</Badge>
            {!isSchritte && stats.phrases > 0 && (
              <Badge variant="outline" className="text-xs">{stats.phrases} shprehje</Badge>
            )}
            <Button variant="outline" size="sm" className="border-0 bg-white shadow-sm hover:bg-zinc-100" asChild>
              <Link to="/unknown-words" className="inline-flex items-center gap-1">
                <BookmarkIcon className="w-3.5 h-3.5" />
                {Object.keys(unknownByWord).length} të ruajtura
              </Link>
            </Button>
          </div>
        </PageHeader>

        <div className="mb-6 flex flex-wrap gap-2">
          {Object.values(VOCAB_SETS).map((set) => (
            <Button
              key={set.id}
              size="sm"
              variant={vocabSet === set.id ? 'default' : 'outline'}
              className={vocabSet === set.id
                ? 'border-0 bg-zinc-950 text-[#FFCE00] shadow-sm hover:bg-zinc-950 hover:text-[#FFCE00]'
                : 'border-0 bg-white text-zinc-700 shadow-sm hover:bg-zinc-100 hover:text-zinc-950'}
              onClick={() => handleSetChange(set.id)}
            >
              {set.level} — {set.book.split(' ').slice(-2).join(' ')}
            </Button>
          ))}
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-5 space-y-4">
          {isSchritte ? (
            <div className={`grid grid-cols-1 ${courses.length > 1 ? 'sm:grid-cols-2' : ''} gap-3`}>
              {courses.length > 1 && (
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-brand-muted mb-1 block">
                    Kursi
                  </label>
                  <div className="relative">
                    <select
                      value={selectedCourse}
                      onChange={(e) => {
                        setSelectedCourse(e.target.value);
                        setSelectedLesson('all');
                      }}
                      className={selectClassName}
                    >
                      <option value="all">Të gjitha leksionet</option>
                      {courses.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.level} — Lektion {c.lessons[0]?.number}–{c.lessons[c.lessons.length - 1]?.number}
                        </option>
                      ))}
                    </select>
                    <ChevronDownIcon className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                  </div>
                </div>
              )}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-brand-muted mb-1 block">
                  Lektion
                </label>
                <div className="relative">
                  <select
                    value={selectedLesson}
                    onChange={(e) => setSelectedLesson(e.target.value)}
                    className={selectClassName}
                  >
                    <option value="all">Të gjitha leksionet</option>
                    {schritteLessons.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.title} ({l.words.length} fjalë)
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-brand-muted mb-1 block">
                  Kapitulli
                </label>
                <div className="relative">
                  <select
                    value={selectedChapter}
                    onChange={(e) => {
                      setSelectedChapter(e.target.value);
                      setSelectedModule('all');
                    }}
                    className={selectClassName}
                  >
                    <option value="all">Të gjitha kapitujt</option>
                    {B2_ASPEKTE_CHAPTERS.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.title}
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-brand-muted mb-1 block">
                  Moduli
                </label>
                <div className="relative">
                  <select
                    value={selectedModule}
                    onChange={(e) => setSelectedModule(e.target.value)}
                    disabled={selectedChapter === 'all'}
                    className={selectClassName}
                  >
                    <option value="all">Të gjithë modulet</option>
                    {modules.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.title} ({m.words.length} fjalë)
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="w-4 h-4 text-brand-secondary absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                type="text"
                placeholder="Kërko fjalë ose përkthim..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="border-0 bg-white pl-9 shadow-sm focus-visible:ring-2"
              />
            </div>
            {!isSchritte && (
              <label className="flex items-center gap-2 text-sm text-brand-text/70 cursor-pointer whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={includePhrases}
                  onChange={(e) => setIncludePhrases(e.target.checked)}
                  className="rounded border-brand-secondary/40 text-brand-primary focus:ring-brand-primary cursor-pointer"
                />
                Përfshi shprehjet
              </label>
            )}
          </div>
          </CardContent>
        </Card>

        {/* Mode tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {MODES.map(({ id, label, icon: Icon }) => (
            <Button
              key={id}
              size="sm"
              variant={mode === id ? 'default' : 'outline'}
              className={mode === id
                ? 'border-0 bg-zinc-950 text-[#FFCE00] shadow-sm hover:bg-[#DD0000] hover:text-white'
                : 'border-0 bg-white text-zinc-700 shadow-sm hover:bg-zinc-100 hover:text-zinc-950'}
              onClick={() => setMode(id)}
            >
              <Icon className="w-4 h-4" />
              {label}
            </Button>
          ))}
        </div>

        {mode === 'browse' && (
          <>
            <BrowseList
              entries={pageEntries}
              totalResults={filtered.length}
              isSchritte={isSchritte}
              onSaveUnknown={saveUnknown}
              unknownByWord={unknownByWord}
              unknownLoading={unknownLoading}
              selectedChapter={selectedChapter}
            />
            {pageCount > 1 && (
              <nav
                aria-label="Paginimi i fjalorit"
                className="mt-6 flex flex-col items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 shadow-sm sm:flex-row"
              >
                <span className="text-xs text-muted-foreground">
                  Shfaqen {(currentPage - 1) * VOCAB_PAGE_SIZE + 1}–
                  {Math.min(currentPage * VOCAB_PAGE_SIZE, filtered.length)} nga {filtered.length} rezultate
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-0 bg-white shadow-sm hover:bg-zinc-100"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    aria-label="Faqja e mëparshme"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                    Mbrapa
                  </Button>
                  <span className="min-w-20 text-center text-xs font-medium text-muted-foreground">
                    Faqja {currentPage} nga {pageCount}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-0 bg-white shadow-sm hover:bg-zinc-100"
                    disabled={currentPage === pageCount}
                    onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}
                    aria-label="Faqja tjetër"
                  >
                    Tjetra
                    <ChevronRightIcon className="h-4 w-4" />
                  </Button>
                </div>
              </nav>
            )}
          </>
        )}
        {mode === 'flashcards' && (
          <Flashcards
            entries={filtered}
            isSchritte={isSchritte}
            onSaveUnknown={saveUnknown}
            unknownByWord={unknownByWord}
          />
        )}
        {mode === 'quiz' && (
          <QuizMode entries={filtered} user={user} updateUserStats={updateUserStats} />
        )}
      </div>
    </DashboardLayout>
  );
};

const BrowseList = ({ entries, totalResults, isSchritte, onSaveUnknown, unknownByWord, unknownLoading, selectedChapter }) => {
  if (entries.length === 0) {
    return (
      <div className="text-center py-16 text-brand-text/40">
        <BookOpenIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="font-medium">Nuk u gjet asnjë fjalë.</p>
      </div>
    );
  }

  // For B2 Aspekte: group entries by module so headers appear between groups
  if (!isSchritte) {
    if (selectedChapter === 'all') {
      const groups = [];
      const seenChapterIds = new Map(); // chapterId -> group index

      entries.forEach((entry) => {
        const key = entry.chapterId ?? 'unknown_chapter';
        if (!seenChapterIds.has(key)) {
          seenChapterIds.set(key, groups.length);
          groups.push({
            chapterId: key,
            chapterNumber: entry.chapterNumber,
            words: []
          });
        }
        groups[seenChapterIds.get(key)].words.push(entry);
      });

      return (
        <div className="space-y-8">
          <p className="text-xs text-brand-secondary font-bold uppercase tracking-widest">
            {totalResults} rezultate
          </p>
          {groups.map((group) => (
            <div key={group.chapterId}>
              {/* Chapter header */}
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs font-black uppercase tracking-widest text-brand-primary bg-brand-primary/10 border border-brand-primary/20 px-3 py-1 rounded-full">
                  Kapitel {group.chapterNumber}
                </span>
                <span className="text-xs text-brand-muted">{group.words.length} fjalë</span>
                <div className="flex-1 h-px bg-brand-primary/10" />
              </div>

              <div className="space-y-2">
                {group.words.map((entry, i) => (
                  <Card key={`${entry.de}-${i}`} className="cursor-default">
                    <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
                      <div className="flex-1 flex items-center gap-2">
                        <span className="text-sm font-bold text-brand-text">{entry.de}</span>
                        <SpeakButton text={entry.de} lang="de-DE" />
                        {entry.module && (
                          <span className="text-[9px] font-bold uppercase tracking-widest text-brand-primary bg-brand-primary/10 px-2 py-0.5 rounded-full border border-brand-primary/20">
                            M{entry.module}
                          </span>
                        )}
                        {entry.type === 'phrase' && (
                          <span className="text-[9px] font-bold uppercase tracking-widest text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full border border-orange-200">
                            Shprehje
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-brand-text/60 sm:text-right sm:max-w-xs">
                        {entry.sq || <span className="italic text-brand-text/30">—</span>}
                      </div>
                      <UnknownWordToggle
                        saved={Boolean(unknownByWord?.[entry.de])}
                        disabled={unknownLoading}
                        onClick={() => onSaveUnknown(entry)}
                      />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    } else {
      const groups = [];
      const seenModuleIds = new Map(); // moduleId -> group index

      entries.forEach((entry) => {
        const key = entry.moduleId ?? '__phrases__';
        if (!seenModuleIds.has(key)) {
          seenModuleIds.set(key, groups.length);
          groups.push({ moduleId: key, moduleNumber: entry.module, moduleTitle: entry.moduleTitle, words: [] });
        }
        groups[seenModuleIds.get(key)].words.push(entry);
      });

      // Derive module title from the first entry's module number
      const getModuleLabel = (group) => {
        if (group.moduleId === '__phrases__') return 'Shprehje';
        if (group.moduleNumber === 5) return 'Wichtige Wortverbindungen';
        return group.moduleNumber ? `Modul ${group.moduleNumber}` : 'Fjalë';
      };

      return (
        <div className="space-y-8">
          <p className="text-xs text-brand-secondary font-bold uppercase tracking-widest">
            {totalResults} rezultate
          </p>
          {groups.map((group) => (
            <div key={group.moduleId}>
              {/* Module header */}
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs font-black uppercase tracking-widest text-brand-primary bg-brand-primary/10 border border-brand-primary/20 px-3 py-1 rounded-full">
                  {getModuleLabel(group)}
                </span>
                <span className="text-xs text-brand-muted">{group.words.length} fjalë</span>
                <div className="flex-1 h-px bg-brand-primary/10" />
              </div>

              <div className="space-y-2">
                {group.words.map((entry, i) => (
                  <Card key={`${entry.de}-${i}`} className="cursor-default">
                    <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
                      <div className="flex-1 flex items-center gap-2">
                        <span className="text-sm font-bold text-brand-text">{entry.de}</span>
                        <SpeakButton text={entry.de} lang="de-DE" />
                        {entry.type === 'phrase' && (
                          <span className="text-[9px] font-bold uppercase tracking-widest text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full border border-orange-200">
                            Shprehje
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-brand-text/60 sm:text-right sm:max-w-xs">
                        {entry.sq || <span className="italic text-brand-text/30">—</span>}
                      </div>
                      <UnknownWordToggle
                        saved={Boolean(unknownByWord?.[entry.de])}
                        disabled={unknownLoading}
                        onClick={() => onSaveUnknown(entry)}
                      />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }
  }

  // Schritte: flat list with lesson badge
  return (
    <div className="space-y-2">
      <p className="text-xs text-brand-secondary font-bold uppercase tracking-widest mb-3">
        {totalResults} rezultate
      </p>
      {entries.map((entry, i) => (
        <Card
          key={`${entry.de}-${i}`}
          className="cursor-default"
        >
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
          <div className="flex-1 flex items-center gap-2">
            <span className="text-sm font-bold text-brand-text">{entry.de}</span>
            <SpeakButton text={entry.de} lang="de-DE" />
            {entry.lesson && (
              <span className="text-[9px] font-bold uppercase tracking-widest text-brand-primary bg-brand-primary/10 px-2 py-0.5 rounded-full border border-brand-primary/20">
                L{entry.lesson}
              </span>
            )}
          </div>
          <div className="text-sm text-brand-text/60 sm:text-right sm:max-w-xs">
            {entry.sq || <span className="italic text-brand-text/30">—</span>}
          </div>
          <UnknownWordToggle
            saved={Boolean(unknownByWord?.[entry.de])}
            disabled={unknownLoading}
            onClick={() => onSaveUnknown(entry)}
          />
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const Flashcards = ({ entries, isSchritte, onSaveUnknown, unknownByWord }) => {
  const [deck, setDeck] = useState(() => shuffleArray(entries));
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [known, setKnown] = useState(new Set());

  const reshuffle = useCallback(() => {
    setDeck(shuffleArray(entries));
    setIndex(0);
    setFlipped(false);
    setKnown(new Set());
  }, [entries]);

  React.useEffect(() => {
    reshuffle();
  }, [entries, reshuffle]);

  if (entries.length === 0) {
    return (
      <div className="text-center py-16 text-brand-text/50">
        <p>Zgjidhni të paktën një {isSchritte ? 'leksion' : 'kapitull'} me fjalë.</p>
      </div>
    );
  }

  const card = deck[index];
  const progress = `${index + 1} / ${deck.length}`;
  const saved = Boolean(unknownByWord?.[card?.de]);

  const next = () => {
    setFlipped(false);
    setIndex((i) => (i + 1 >= deck.length ? 0 : i + 1));
  };

  const prev = () => {
    setFlipped(false);
    setIndex((i) => (i - 1 < 0 ? deck.length - 1 : i - 1));
  };



  return (
    <div className="max-w-lg mx-auto">
      <div className="flex justify-between items-center mb-4">
        <span className="text-xs font-bold text-brand-secondary uppercase tracking-widest">
          {progress} • {known.size} të mësuar
        </span>
        <button
          onClick={reshuffle}
          className="flex items-center gap-1 text-xs font-bold text-brand-text/60 hover:text-brand-primary transition-colors duration-200 cursor-pointer"
        >
          <ArrowPathIcon className="w-4 h-4" /> Përziej
        </button>
      </div>
      <Card
        className="w-full min-h-[240px] cursor-pointer transition-all duration-200"
        onClick={() => setFlipped(!flipped)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setFlipped(!flipped);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <CardContent className="p-8 flex flex-col items-center justify-center min-h-[240px]">
        <span className="text-[10px] font-bold uppercase tracking-widest text-brand-primary mb-4">
          {flipped ? (card.sq ? 'Shqip' : 'Shprehje') : 'Deutsch'}
        </span>
        <p className="text-2xl font-display font-bold text-brand-text text-center leading-relaxed flex items-center gap-2 justify-center">
          {flipped ? (card.sq || card.de) : card.de}
          {(!flipped || !card.sq) && <SpeakButton text={card.de} lang="de-DE" />}
        </p>
        <span className="text-xs text-brand-text/40 mt-6">Kliko për të kthyer</span>
        </CardContent>
      </Card>

      <div className="flex justify-center mt-4">
        <UnknownWordToggle saved={saved} onClick={() => onSaveUnknown(card)} />
      </div>

      {flipped ? (
        <div className="mt-5 space-y-2">
          <p className="text-center text-[11px] font-bold text-zinc-500 uppercase tracking-wider">
            Vlerëso kujtesën (Sistemi SRS):
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { grade: SRS_GRADES.AGAIN, ...GRADE_LABELS[SRS_GRADES.AGAIN] },
              { grade: SRS_GRADES.HARD,  ...GRADE_LABELS[SRS_GRADES.HARD] },
              { grade: SRS_GRADES.GOOD,  ...GRADE_LABELS[SRS_GRADES.GOOD] },
              { grade: SRS_GRADES.EASY,  ...GRADE_LABELS[SRS_GRADES.EASY] },
            ].map(({ grade, label, time, color }) => (
              <button
                key={grade}
                type="button"
                onClick={() => {
                  reviewWord(card.de, grade);
                  if (grade >= SRS_GRADES.GOOD) {
                    setKnown((k) => new Set([...k, card.de]));
                  }
                  next();
                }}
                className={`py-2 px-3 rounded-xl border-0 text-xs font-bold transition-all shadow-sm flex flex-col items-center justify-center ${color}`}
              >
                <span>{label}</span>
                <span className="text-[10px] opacity-75 font-normal">({time})</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex gap-3 mt-6">
          <Button
            variant="outline"
            onClick={prev}
            className="flex-1 border-0 bg-white shadow-sm hover:bg-zinc-100"
          >
            <ChevronLeftIcon className="w-4 h-4" /> Mbrapa
          </Button>
          <Button
            onClick={() => setFlipped(true)}
            className="flex-1 border-0 bg-[#FFCE00] font-bold text-zinc-950 shadow-sm hover:bg-[#F2C300]"
          >
            Shiko Përgjigjen
          </Button>
          <Button
            onClick={next}
            className="flex-1 border-0 bg-zinc-950 text-[#FFCE00] shadow-sm hover:bg-[#DD0000] hover:text-white"
          >
            Kalo <ChevronRightIcon className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

const QuizMode = ({ entries, user, updateUserStats }) => {
  const quizEntries = useMemo(() => entries.filter((e) => e.sq), [entries]);
  const [started, setStarted] = useState(false);
  const [deck, setDeck] = useState([]);
  const [index, setIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [finished, setFinished] = useState(false);
  const TOTAL = Math.min(15, quizEntries.length);

  const start = () => {
    if (quizEntries.length < 4) return;
    setDeck(shuffleArray(quizEntries).slice(0, TOTAL));
    setIndex(0);
    setScore(0);
    setFeedback(null);
    setFinished(false);
    setStarted(true);
  };

  const current = deck[index];

  const options = useMemo(() => {
    if (!current) return [];
    const wrong = shuffleArray(quizEntries.filter((e) => e.de !== current.de)).slice(0, 3);
    return shuffleArray([current, ...wrong]);
  }, [current, quizEntries]);

  const handleAnswer = (option) => {
    if (feedback) return;
    const correct = option.de === current.de;
    const newScore = correct ? score + 10 : score;
    if (correct) setScore(newScore);
    setFeedback(correct ? 'correct' : 'wrong');

    setTimeout(() => {
      if (index + 1 >= deck.length) {
        finishQuiz(newScore);
      } else {
        setIndex((i) => i + 1);
        setFeedback(null);
      }
    }, 1200);
  };

  const finishQuiz = async (finalScore) => {
    const totalXp = (user?.xp || 0) + finalScore;
    await updateUserStats({ xp: totalXp });
    setScore(finalScore);
    setFinished(true);
  };

  if (quizEntries.length < 4) {
    return (
      <div className="text-center py-16 text-brand-text/50">
        <p>Duhen të paktën 4 fjalë për kuizin.</p>
      </div>
    );
  }

  if (!started) {
    return (
      <Card className="max-w-md mx-auto">
        <CardContent className="p-10 text-center">
        <SparklesIcon className="w-10 h-10 text-brand-text mx-auto mb-4" />
        <h2 className="text-lg font-bold mb-2">Kuiz i Shpejtë</h2>
        <p className="text-sm text-brand-muted mb-6">
          {TOTAL} pyetje — zgjidh përkthimin e saktë shqip për fjalën gjermane.
        </p>
        <Button type="button" onClick={start}>
          Fillo Kuizin
        </Button>
        </CardContent>
      </Card>
    );
  }

  if (finished) {
    return (
      <Card className="max-w-md mx-auto">
        <CardContent className="p-10 text-center">
        <CheckCircleIcon className="w-12 h-12 text-brand-cta mx-auto mb-4" />
        <h2 className="text-xl font-bold text-brand-text mb-2">Kuiz i Përfunduar!</h2>
        <p className="text-3xl font-bold text-brand-text mb-2">{score} XP</p>
        <p className="text-sm text-brand-muted mb-6">
          {Math.round((score / (TOTAL * 10)) * 100)}% saktësi
        </p>
        <Button type="button" onClick={start}>
          Provo Përsëri
        </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <span className="text-xs font-bold text-brand-text/50 uppercase tracking-widest">
          Pyetja {index + 1} / {deck.length}
        </span>
        <span className="text-sm font-black text-brand-primary">{score} XP</span>
      </div>

      <Card
        className={cn(
          'mb-6',
          feedback === 'correct' && 'bg-green-50',
          feedback === 'wrong' && 'bg-red-50',
        )}
      >
        <CardContent className="p-8 text-center">
        <span className="text-[10px] font-black uppercase tracking-widest text-brand-text/50 block mb-3">
          Çfarë do të thotë?
        </span>
        <p className="text-2xl font-black text-brand-text">{current.de}</p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {options.map((opt, i) => (
          <Button
            key={i}
            variant="outline"
            onClick={() => handleAnswer(opt)}
            disabled={!!feedback}
            className={cn(
              'border-0 bg-white p-4 h-auto text-sm font-bold text-left justify-start whitespace-normal shadow-sm hover:bg-zinc-50',
              feedback && opt.de === current.de && 'bg-green-50 text-green-800 hover:bg-green-50',
              feedback && opt.de !== current.de && 'opacity-50',
            )}
          >
            {opt.sq}
          </Button>
        ))}
      </div>

      {feedback === 'wrong' && (
        <p className="text-center text-sm text-red-600 mt-4 font-medium">
          Përgjigja e saktë: {current.sq}
        </p>
      )}
    </div>
  );
};

export default VocabularyPage;
